import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Send, History, Repeat, ImagePlus, X, Trash2, Plus, Pencil, Cake, AlertTriangle, Users, Building2, BellRing, CreditCard, Wallet, Moon } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import {
  PageHeader, Tabs, Card, CardBody, Field, Input, Textarea, Select, Button, Badge, Switch,
  DataTable, Modal, ConfirmDialog, EmptyState, LoadingBlock, Avatar,
} from '../../ui';

const AUDIENCES = ['all', 'male', 'female', 'phones'];
const TTL = ['', '6', '24', '72', '168'];
const RULE_TYPES = ['birthday', 'negative_balance'];
const RULE_ICONS = { birthday: Cake, negative_balance: AlertTriangle };
const parsePhones = (text) => [...new Set(String(text).split(/[\s,;]+/).map((p) => p.replace(/\D/g, '')).filter((p) => p.length >= 9))];

/** Рассылки: кабинет компании и консоль владельца (владелец выбирает компанию) */
export default function NotificationsPage() {
  const { t } = useI18n();
  const { isOwner, isViewingAsOwner, canEdit, company } = useSession();
  const platformWide = isOwner && !isViewingAsOwner;
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || (canEdit ? 'compose' : 'history');
  const companyId = platformWide ? params.get('company') || '' : String(company?.id || '');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (!platformWide) return;
    api.get('/admin/tenants').then((res) => setCompanies(res.data.data || [])).catch(() => {});
  }, [platformWide]);

  const headers = useMemo(() => (platformWide && companyId ? { 'X-Admin-Tenant-ID': companyId } : {}), [platformWide, companyId]);
  const companyName = platformWide ? companies.find((c) => String(c.id) === companyId)?.name : company?.name;

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const needsCompany = platformWide && !companyId && tab !== 'history';

  return (
    <>
      <PageHeader
        title={t('push.title')}
        description={t('push.subtitle')}
        actions={platformWide && (
          <div className="w-56">
            <Select value={companyId} onChange={(e) => setParam('company', e.target.value)} aria-label={t('push.company')}>
              <option value="">{tab === 'history' ? t('analytics.allCompanies') : t('push.pickCompany')}</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
        )}
      />

      <Tabs
        className="mb-6"
        value={tab}
        onChange={(v) => setParam('tab', v)}
        items={[
          ...(canEdit ? [{ id: 'compose', label: t('push.tabs.compose'), icon: Send }] : []),
          { id: 'history', label: t('push.tabs.history'), icon: History },
          { id: 'rules', label: t('push.tabs.rules'), icon: Repeat },
        ]}
      />

      {needsCompany ? (
        <Card><EmptyState icon={Building2} title={t('push.pickCompany')} text={t('push.pickCompanyHint')} /></Card>
      ) : (
        <>
          {tab === 'compose' && canEdit && <Compose key={companyId} headers={headers} companyName={companyName} onSent={() => setParam('tab', 'history')} />}
          {tab === 'history' && <HistoryTab key={companyId} headers={headers} showCompany={platformWide && !companyId} />}
          {tab === 'rules' && (
            <>
              <CardEventsSection key={`ce-${companyId}`} companyId={companyId} />
              <RulesTab key={companyId} headers={headers} />
            </>
          )}
        </>
      )}
    </>
  );
}

function Compose({ headers, companyName, onSent }) {
  const { t, fmt } = useI18n();
  const [form, setForm] = useState({ title: '', message: '', audience: 'all', phones: '', image_url: '', ttl: '' });
  const [audience, setAudience] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/admin/push/audience', { headers }).then((res) => setAudience(res.data)).catch(() => setAudience(null));
  }, [headers]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const phones = parsePhones(form.phones);
  const count = form.audience === 'phones' ? phones.length : audience?.[form.audience] ?? 0;
  const valid = form.title.trim() && form.message.trim() && count > 0;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await api.post('/admin/upload', body, { headers });
      setForm((f) => ({ ...f, image_url: res.data.url }));
    } catch (err) {
      toast.error(errorText(err, t('push.uploadFailed')));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        target: form.audience,
        ...(form.audience === 'phones' ? { phones } : {}),
        ...(form.image_url ? { image_url: form.image_url } : {}),
        ...(form.ttl ? { ttl_hours: Number(form.ttl) } : {}),
      };
      const res = await api.post('/admin/send-push-manual', payload, { headers });
      setConfirm(false);
      if (!res.data.sent) {
        toast.error(t('push.noRecipients'));
        return;
      }
      toast.success(t('push.sent', { count: fmt.number(res.data.sent), pushed: fmt.number(res.data.delivered ?? res.data.pushed ?? res.data.sent) }));
      onSent();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <Card>
        <CardBody className="space-y-5">
          <Field label={t('push.audience')}>
            <div role="radiogroup" className="flex flex-wrap gap-2">
              {AUDIENCES.map((a) => {
                const active = form.audience === a;
                const n = a === 'phones' ? null : audience?.[a];
                return (
                  <button
                    key={a}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setForm({ ...form, audience: a })}
                    className={clsx(
                      'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors',
                      active
                        ? 'border-brand-500 bg-brand-50 text-brand-800 dark:border-brand-400 dark:bg-brand-950/40 dark:text-brand-200'
                        : 'border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800'
                    )}
                  >
                    {t(`push.audiences.${a}`)}
                    {n !== null && n !== undefined && <span className="tabular text-xs text-zinc-500">{fmt.number(n)}</span>}
                  </button>
                );
              })}
            </div>
          </Field>

          {form.audience === 'phones' && (
            <Field label={t('push.phones')} hint={t('push.phonesHint')}>
              <Textarea value={form.phones} onChange={set('phones')} rows={3} className="font-mono text-[13px]" placeholder="380501234567" />
            </Field>
          )}

          <Field label={t('push.titleField')} required hint={`${form.title.length}/100`}>
            <Input value={form.title} onChange={set('title')} maxLength={100} />
          </Field>
          <Field label={t('push.message')} required hint={`${form.message.length}/500`}>
            <Textarea value={form.message} onChange={set('message')} maxLength={500} rows={4} />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t('push.image')} hint={t('push.imageHint')}>
              {form.image_url ? (
                <div className="flex items-center gap-3">
                  <img src={form.image_url} alt="" className="h-12 w-12 rounded-lg border border-zinc-200 object-cover dark:border-zinc-700" />
                  <Button variant="ghost" size="sm" icon={X} onClick={() => setForm({ ...form, image_url: '' })}>{t('push.removeImage')}</Button>
                </div>
              ) : (
                <>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => upload(e.target.files?.[0])} />
                  <Button variant="secondary" icon={ImagePlus} loading={uploading} onClick={() => fileRef.current?.click()}>{t('documents.upload')}</Button>
                </>
              )}
            </Field>
            <Field label={t('push.ttl')} hint={t('push.ttlHint')}>
              <Select value={form.ttl} onChange={set('ttl')}>
                {TTL.map((v) => <option key={v} value={v}>{t(`push.ttlOptions.${v}`)}</option>)}
              </Select>
            </Field>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-5 dark:border-zinc-800">
            <span className="inline-flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
              <Users className="h-4 w-4" />
              <span className="tabular">{t('push.recipients', { count: fmt.number(count) })}</span>
            </span>
            <Button icon={Send} disabled={!valid} onClick={() => setConfirm(true)}>{t('push.send')}</Button>
          </div>
        </CardBody>
      </Card>

      <div>
        <p className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('push.preview')}</p>
        <PushPreview title={form.title} message={form.message} image={form.image_url} appName={companyName} />
      </div>

      <ConfirmDialog
        open={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={send}
        loading={sending}
        tone="neutral"
        title={t('push.confirmTitle')}
        text={t('push.confirmText', { count: fmt.number(count) })}
        confirmLabel={t('push.send')}
      />
    </div>
  );
}

function PushPreview({ title, message, image, appName }) {
  const { t } = useI18n();
  return (
    <div className="rounded-[2rem] bg-gradient-to-b from-zinc-700 to-zinc-900 p-4 shadow-pop">
      <p className="tabular mb-6 mt-2 text-center text-4xl font-light text-white/90">9:41</p>
      <div className="rounded-2xl bg-white/85 p-3 backdrop-blur dark:bg-zinc-800/90">
        <div className="flex items-start gap-2.5">
          <Avatar name={appName || 'App'} size="sm" className="rounded-md" />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="truncate text-[11px] uppercase tracking-wide text-zinc-500">{appName || 'App'}</p>
              <p className="shrink-0 text-[11px] text-zinc-500">{t('push.now')}</p>
            </div>
            <p className="mt-0.5 break-words text-sm font-semibold text-zinc-900 dark:text-zinc-50">{title || t('push.previewTitle')}</p>
            <p className="mt-0.5 line-clamp-4 whitespace-pre-line break-words text-sm text-zinc-700 dark:text-zinc-300">{message || t('push.previewMessage')}</p>
          </div>
          {image && <img src={image} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" />}
        </div>
      </div>
    </div>
  );
}

function HistoryTab({ headers, showCompany }) {
  const { t, fmt } = useI18n();
  const { canEdit } = useSession();
  const [rows, setRows] = useState(null);
  const [system, setSystem] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/push/history', { headers })
      .then((res) => { setRows(res.data.notifications || []); setSystem(res.data.system || []); })
      .catch((err) => { setRows([]); toast.error(errorText(err, t('common.error'))); });
  }, [headers, t]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/push/notification/${deleting.id}`, { headers });
      toast.success(t('push.deleted'));
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'title',
      header: t('push.titleField'),
      render: (r) => (
        <div className="flex min-w-[14rem] items-start gap-3">
          {r.image_url && <img src={r.image_url} alt="" className="h-9 w-9 shrink-0 rounded-md object-cover" />}
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">{r.title}</p>
            <p className="line-clamp-2 max-w-md text-xs text-zinc-500">{r.message}</p>
            {r.group_id?.startsWith('rule-') && <Badge tone="brand" className="mt-1">{t('push.tabs.rules')}</Badge>}
          </div>
        </div>
      ),
    },
    ...(showCompany ? [{ key: 'tenant_name', header: t('employees.company'), sortable: true, hideOnMobile: true }] : []),
    { key: 'sent_at', header: t('push.sentAt'), sortable: true, render: (r) => <span className="text-zinc-500" title={fmt.dateTime(r.sent_at)}>{fmt.relative(r.sent_at)}</span> },
    { key: 'sent_count', header: t('push.delivered'), sortable: true, align: 'right', render: (r) => <span className="tabular">{fmt.number(r.sent_count)}</span> },
    {
      key: 'read_count',
      header: t('push.readRate'),
      sortable: true,
      sortValue: (r) => (r.sent_count ? r.read_count / r.sent_count : 0),
      hideOnMobile: true,
      render: (r) => {
        const pct = r.sent_count ? Math.round((r.read_count / r.sent_count) * 100) : 0;
        return (
          <div className="flex w-32 items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
              <div className="h-full rounded-full bg-[#2a78d6] dark:bg-[#3987e5]" style={{ width: `${pct}%` }} />
            </div>
            <span className="tabular w-9 text-right text-xs text-zinc-600 dark:text-zinc-400">{pct}%</span>
          </div>
        );
      },
    },
    ...(canEdit ? [{
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (r) => <Button variant="ghost" size="icon-sm" icon={Trash2} aria-label={t('common.delete')} onClick={() => setDeleting(r)} />,
    }] : []),
  ];

  return (
    <>
      {system.length > 0 && (
        <Card className="mb-6">
          <CardBody>
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('push.systemTitle')}</p>
            <p className="mt-0.5 text-xs text-zinc-500">{t('push.systemHint')}</p>
            <dl className="mt-4 grid gap-4 sm:grid-cols-3">
              {['new_card', 'top_up', 'negative_balance'].map((type) => {
                const s = system.find((x) => x.type === type);
                return (
                  <div key={type}>
                    <dt className="text-xs text-zinc-500">{t(`push.systemTypes.${type}`)}</dt>
                    <dd className="mt-1 flex items-baseline gap-2">
                      <span className="tabular text-xl font-semibold text-zinc-900 dark:text-zinc-100">{fmt.number(s?.count || 0)}</span>
                      {s?.count ? <span className="tabular text-xs text-zinc-500">{t('push.systemRead', { pct: Math.round((s.read_count / s.count) * 100) })}</span> : null}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </CardBody>
        </Card>
      )}
      <DataTable columns={columns} rows={rows || []} loading={!rows} rowKey={(r) => r.group_id} empty={<EmptyState icon={BellRing} title={t('push.emptyHistory')} />} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        loading={busy}
        title={t('push.deleteTitle')}
        text={t('push.deleteText')}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}

const CARD_EVENTS = [
  { type: 'top_up', icon: Wallet },
  { type: 'new_card', icon: CreditCard },
  { type: 'negative_balance', icon: AlertTriangle },
];

/** Як побачить співробітник: ті самі підстановки, що й на сервері (services/cardEvents.js) */
function renderCardSample(template, t) {
  const text = String(template || '')
    .replace(/\{name\}/g, t('push.cardEvents.sample.name'))
    .replace(/\{card\}/g, '0777')
    .replace(/\{amount\}/g, '+1 000 ₴')
    .replace(/\{balance\}/g, '1 250 ₴')
    .replace(/[ \t]+/g, ' ')
    .replace(/\s+([,!.?:])/g, '$1')
    .replace(/^[\s,]+/, '')
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/** Автоматичні сповіщення про картку: нова, поповнення, мінус; тексти й тихі години */
function CardEventsSection({ companyId }) {
  const { t } = useI18n();
  const { canEdit } = useSession();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/admin/tenants/${companyId}/card-events`)
      .then((res) => { setForm(res.data.data); setSaved(res.data.data); })
      .catch(() => setForm(null));
  }, [companyId]);

  if (!form) return null;
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const setType = (type, patch) => setForm((f) => ({ ...f, [type]: { ...f[type], ...patch } }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, top_up: { ...form.top_up, min_amount: Number(form.top_up.min_amount) || 0 } };
      const res = await api.put(`/admin/tenants/${companyId}/card-events`, body);
      setForm(res.data.data);
      setSaved(res.data.data);
      toast.success(t('common.saved'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-8">
      <CardBody className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('push.cardEvents.title')}</p>
            <p className="mt-0.5 max-w-2xl text-xs text-zinc-500">{t('push.cardEvents.hint')}</p>
          </div>
          {canEdit && <Button onClick={save} loading={busy} disabled={!dirty}>{t('common.save')}</Button>}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {CARD_EVENTS.map(({ type, icon: Icon }) => {
            const conf = form[type];
            return (
              <div key={type} className={clsx('space-y-3 rounded-xl border border-zinc-200 p-4 dark:border-zinc-800', !conf.enabled && 'opacity-60')}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0 text-brand-600" />
                    <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t(`push.cardEvents.types.${type}`)}</p>
                  </div>
                  <Switch checked={conf.enabled} onChange={(v) => setType(type, { enabled: v })} disabled={!canEdit} label={t(`push.cardEvents.types.${type}`)} />
                </div>
                <Field label={t('push.titleField')}>
                  <Input value={conf.title} onChange={(e) => setType(type, { title: e.target.value })} maxLength={120} disabled={!canEdit} />
                </Field>
                <Field label={t('push.message')}>
                  <Textarea rows={2} value={conf.message} onChange={(e) => setType(type, { message: e.target.value })} maxLength={300} disabled={!canEdit} />
                </Field>
                {type === 'top_up' && (
                  <Field label={t('push.cardEvents.minAmount')} hint={t('push.cardEvents.minAmountHint')}>
                    <Input type="number" min={0} step="1" value={conf.min_amount} onChange={(e) => setType(type, { min_amount: e.target.value })} disabled={!canEdit} />
                  </Field>
                )}
                <div className="rounded-lg bg-zinc-50 p-3 text-xs dark:bg-zinc-900">
                  <p className="font-semibold text-zinc-900 dark:text-zinc-100">{renderCardSample(conf.title, t)}</p>
                  <p className="mt-0.5 text-zinc-600 dark:text-zinc-400">{renderCardSample(conf.message, t)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-zinc-500">{t('push.cardEvents.placeholders')}</p>

        <div className="flex flex-wrap items-end gap-4 border-t border-zinc-200 pt-4 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <Moon className="h-4 w-4 text-brand-600" />
            <Switch checked={form.quiet_hours.enabled} onChange={(v) => setForm((f) => ({ ...f, quiet_hours: { ...f.quiet_hours, enabled: v } }))} disabled={!canEdit} label={t('push.cardEvents.quiet')} />
            <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('push.cardEvents.quiet')}</span>
          </div>
          <Field label={t('push.cardEvents.quietFrom')} className="w-32">
            <Input type="time" value={form.quiet_hours.from} onChange={(e) => setForm((f) => ({ ...f, quiet_hours: { ...f.quiet_hours, from: e.target.value } }))} disabled={!canEdit || !form.quiet_hours.enabled} />
          </Field>
          <Field label={t('push.cardEvents.quietTo')} className="w-32">
            <Input type="time" value={form.quiet_hours.to} onChange={(e) => setForm((f) => ({ ...f, quiet_hours: { ...f.quiet_hours, to: e.target.value } }))} disabled={!canEdit || !form.quiet_hours.enabled} />
          </Field>
          <p className="max-w-md text-xs text-zinc-500">{t('push.cardEvents.quietHint')}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function RulesTab({ headers }) {
  const { t, fmt } = useI18n();
  const { canEdit } = useSession();
  const [rules, setRules] = useState(null);
  const [editing, setEditing] = useState(null); // {} — новое правило
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState({});

  const load = useCallback(() => {
    api.get('/admin/push-rules', { headers })
      .then((res) => setRules(res.data.rules || []))
      .catch((err) => { setRules([]); toast.error(errorText(err, t('common.error'))); });
  }, [headers, t]);
  useEffect(() => { load(); }, [load]);

  const save = async (rule, values) => {
    const body = {
      title: values.title,
      message: values.message,
      type: values.type,
      target: values.target || 'all',
      is_active: values.is_active ? 1 : 0,
      is_individual: 1,
    };
    if (rule?.rule_id) await api.put(`/admin/push-rules/${rule.rule_id}`, body, { headers });
    else await api.post('/admin/push-rules', body, { headers });
    toast.success(t('push.ruleSaved'));
    load();
  };

  const toggle = async (rule) => {
    try {
      await save(rule, { ...rule, is_active: !rule.is_active });
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const preview = async (rule) => {
    try {
      const res = await api.get(`/admin/push-rules/${rule.rule_id}/preview`, { headers });
      setPreviews((p) => ({ ...p, [rule.rule_id]: res.data.count }));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/push-rule/${deleting.rule_id}`, { headers });
      toast.success(t('push.ruleDeleted'));
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  if (!rules) return <LoadingBlock />;

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">{t('push.rulesIntro')}</p>
        {canEdit && <Button icon={Plus} onClick={() => setEditing({})}>{t('push.newRule')}</Button>}
      </div>

      {!rules.length ? (
        <Card>
          <EmptyState
            icon={Repeat}
            title={t('push.emptyRules')}
            text={t('push.emptyRulesHint')}
            action={canEdit && <Button icon={Plus} onClick={() => setEditing({ type: 'birthday' })}>{t('push.newRule')}</Button>}
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rules.map((rule) => {
            const Icon = RULE_ICONS[rule.type] || Repeat;
            const known = RULE_TYPES.includes(rule.type);
            return (
              <Card key={rule.rule_id} className={clsx(!rule.is_active && 'opacity-70')}>
                <CardBody className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-300">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{known ? t(`push.ruleTypes.${rule.type}`) : rule.type}</p>
                        <p className="text-xs text-zinc-500">{t(`push.audiences.${rule.target || 'all'}`)}</p>
                      </div>
                    </div>
                    <Switch checked={!!rule.is_active} onChange={() => toggle(rule)} disabled={!canEdit || !known} label={rule.is_active ? t('push.active') : t('push.paused')} />
                  </div>
                  <div className="rounded-lg bg-zinc-50 px-3 py-2.5 dark:bg-zinc-800/60">
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{rule.title}</p>
                    <p className="mt-0.5 whitespace-pre-line text-sm text-zinc-600 dark:text-zinc-400">{rule.message}</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500">
                    <span>
                      {t('push.lastRun')}: {rule.last_run ? fmt.date(rule.last_run) : t('common.never')}
                      {previews[rule.rule_id] !== undefined && <> · <span className="font-medium text-zinc-700 dark:text-zinc-300">{t('push.todayWillGet', { count: previews[rule.rule_id] })}</span></>}
                    </span>
                    <span className="flex items-center gap-1">
                      {known && <Button variant="ghost" size="sm" icon={Users} onClick={() => preview(rule)}>{t('push.checkToday')}</Button>}
                      {canEdit && known && <Button variant="ghost" size="icon-sm" icon={Pencil} aria-label={t('common.edit')} onClick={() => setEditing(rule)} />}
                      {canEdit && <Button variant="ghost" size="icon-sm" icon={Trash2} aria-label={t('common.delete')} onClick={() => setDeleting(rule)} />}
                    </span>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <RuleModal rule={editing} onClose={() => setEditing(null)} onSave={save} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        loading={busy}
        title={t('push.deleteRuleTitle')}
        text={t('push.deleteRuleText', { name: deleting?.title })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}

function RuleModal({ rule, onClose, onSave }) {
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!rule) return;
    setForm({
      type: rule.type || 'birthday',
      target: rule.target || 'all',
      title: rule.title || '',
      message: rule.message || '',
      is_active: rule.rule_id ? !!rule.is_active : true,
    });
  }, [rule]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(rule, { ...form, title: form.title.trim(), message: form.message.trim() });
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <Modal
      open={!!rule && !!form}
      onClose={onClose}
      title={rule?.rule_id ? t('push.editRule') : t('push.newRule')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="rule-form" loading={saving}>{t('common.save')}</Button>
        </>
      }
    >
      {form && (
        <form id="rule-form" onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('push.ruleType')} hint={t(`push.ruleTypeHints.${form.type}`)}>
              <Select value={form.type} onChange={set('type')}>
                {RULE_TYPES.map((type) => <option key={type} value={type}>{t(`push.ruleTypes.${type}`)}</option>)}
              </Select>
            </Field>
            <Field label={t('push.audience')}>
              <Select value={form.target} onChange={set('target')}>
                {AUDIENCES.filter((a) => a !== 'phones').map((a) => <option key={a} value={a}>{t(`push.audiences.${a}`)}</option>)}
              </Select>
            </Field>
          </div>
          <Field label={t('push.titleField')} required>
            <Input value={form.title} onChange={set('title')} maxLength={100} required />
          </Field>
          <Field label={t('push.message')} required hint={t('push.placeholders')}>
            <Textarea value={form.message} onChange={set('message')} maxLength={500} rows={4} required />
          </Field>
          <label className="flex items-center gap-3 text-sm text-zinc-700 dark:text-zinc-300">
            <Switch checked={form.is_active} onChange={(v) => setForm({ ...form, is_active: v })} label={t('push.active')} />
            {form.is_active ? t('push.active') : t('push.paused')}
          </label>
        </form>
      )}
    </Modal>
  );
}
